import React, { useMemo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDGET_HEADING_STYLE } from '../../styles';
import { CategorizationMainView } from './components/CategorizationMainView';
import { TaxSavings } from './components/TaxSavings';
import { CategorizationContext } from '../../contexts/CategorizationContext';
import { validateFeatureFlag } from '../../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../../constants/general';
import { CategorizationWidgetSkeleton } from '../../components/skeletons/CategorizationWidgetSkeleton';
import { DashboardContext } from '../../contexts/DashboardContext';
import { getNumOfTransactionsLeftToCategorize } from '../../utils/categorization.utils';
import { useStore } from '../../../../store';
import { CategorizationZeroState } from './components/CategorizationZeroState';
import { CategorizationPromotion } from './components/CategorizationPromotion';
import Heading from '../../../../generic-components/Heading';
import Icon from '../../../../generic-components/Icon';
import LiliAiIcon from '../../../../assets/icons/svg/lili-ai.svg';

export const testIdPrefix = 'CategorizationWidget';

interface ICategorizationWidget {
  isLoading: boolean;
}

export const CategorizationWidget = ({ isLoading }: ICategorizationWidget) => {
  const { t } = useTranslation();
  const actionFlags = useStore((state) => state.summary.actionFlags);
  const [categorizedExpensesCounter, setCategorizedExpensesCounter] = useState<number>(0);
  const [categorizedIncomeCounter, setCategorizedIncomeCounter] = useState<number>(0);
  const context = useContext(DashboardContext);
  const {
    uncategorizedTransactions: { expenseTransactionsList, incomeTransactionsList },
  } = context || {};
  const { didCategorizeAnyTransaction } = actionFlags || {};
  const showMainView =
    expenseTransactionsList &&
    incomeTransactionsList &&
    Object.keys(expenseTransactionsList).length >= 0 &&
    Object.keys(incomeTransactionsList).length >= 0;
  const showZeroState =
    !didCategorizeAnyTransaction &&
    expenseTransactionsList?.numOfTransactions === 0 &&
    incomeTransactionsList?.numOfTransactions === 0;
  const showCategorizeExpensesTitleOnly =
    !showZeroState &&
    !validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.INCOME) &&
    validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.FEATURE);
  const showTaxSavings = validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.FEATURE) && !showZeroState;
  const showPoweredByLiliAi = validateFeatureFlag(FEATURE_FLAG.CATEGORIZE_AI);
  const showCategorizationPromotion = !validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.FEATURE);
  const { uncategorizedTotalNumOfRecords: uncategorizedExpensesTotalNumOfRecords = 0 } = expenseTransactionsList || {};
  const { uncategorizedTotalNumOfRecords: uncategorizedIncomesTotalNumOfRecords = 0 } = incomeTransactionsList || {};
  const categorizationContextValue = useMemo(
    () => ({
      categorizedExpensesCounter,
      setCategorizedExpensesCounter,
      categorizedIncomeCounter,
      setCategorizedIncomeCounter,
    }),
    [categorizedExpensesCounter, categorizedIncomeCounter],
  );
  if (isLoading) return <CategorizationWidgetSkeleton />;
  const numOfExpensesLeftToCategorize = getNumOfTransactionsLeftToCategorize(uncategorizedExpensesTotalNumOfRecords, categorizedExpensesCounter);
  const numOfIncomeLeftToCategorize = getNumOfTransactionsLeftToCategorize(uncategorizedIncomesTotalNumOfRecords, categorizedIncomeCounter);
  const hasAnyUncategorizedTransactions = numOfExpensesLeftToCategorize + numOfIncomeLeftToCategorize > 0;
  return (
    <CategorizationContext.Provider value={categorizationContextValue}>
      <div className='w-full h-full relative flex flex-col justify-between pt-[24px] overflow-hidden z-49'>
        <div className='flex flex-col items-center'>
          <div className='bg-action01 w-full h-[200px] rounded-t-[10px] absolute z-1 top-0' />
          <div
            className={`flex leading-4 z-10 relative ${showCategorizeExpensesTitleOnly && 'mb-[80px]'}`}
            data-testid={`${testIdPrefix}__title`}
          >
            {showCategorizeExpensesTitleOnly && (
              <>
                <span className={WIDGET_HEADING_STYLE}>{t('WIDGET_CATEGORIZATION.TITLE_EXPENSE')}</span>
                <span className='ml-[5px]'>{`(${numOfExpensesLeftToCategorize})`}</span>
              </>
            )}
            {!showCategorizeExpensesTitleOnly && (
              <span className={WIDGET_HEADING_STYLE}>{t('WIDGET_CATEGORIZATION.TITLE')}</span>
            )}
          </div>
          {showMainView && !showZeroState && !showCategorizationPromotion && (
          <>
            <CategorizationMainView
              expenseTransactionsList={expenseTransactionsList?.transactionList}
              incomeTransactionsList={incomeTransactionsList?.transactionList}
              numOfTotalExpenses={expenseTransactionsList?.uncategorizedTotalNumOfRecords || 0}
              numOfTotalIncome={incomeTransactionsList?.uncategorizedTotalNumOfRecords || 0}
            />
            {showPoweredByLiliAi && hasAnyUncategorizedTransactions && (
            <div className='flex justify-center py-[12px] gap-[4px]'>
              <Heading title={t('POWERED_BY')} className='font-metricR text-[12px] text-bw06' />
              <Icon className='w-[32px]' src={LiliAiIcon} />
            </div>
            )}
          </>
          )}
          {!showCategorizationPromotion && showZeroState && <CategorizationZeroState />}
          {showCategorizationPromotion && <CategorizationPromotion />}
        </div>
        {showTaxSavings && (
          <div className='h-[60px] flex justify-center border-t-[1px] border-bw03 mx-[20px]'>
            <TaxSavings />
          </div>
        )}
      </div>
    </CategorizationContext.Provider>
  );
};
